import { EventEmitter } from 'events';
import { ethers } from 'ethers';
import {
  ContractDeploymentValidator,
  AbiCompatibilityChecker,
  NetworkEnvironmentDetector,
  DevelopmentModeErrorHandler,
  ContractValidationResult as DevContractValidationResult,
} from '../utils/developmentModeHandling';

export interface ContractAddresses {
  swmAddress: string;
  wethAddress: string;
  llmnftAddress: string;
  hfswmAddress: string;
  poolAddress: string;
  usdtAddress?: string;
  receiverAddress?: string;
}

export interface ContractABIs {
  [contractName: string]: any[];
}

export interface ContractDeploymentInfo {
  address: string;
  blockNumber?: number;
  transactionHash?: string;
  timestamp?: number;
}

export interface ContractValidationResult {
  isValid: boolean;
  contractName: string;
  address: string;
  error?: string;
  deploymentInfo?: ContractDeploymentInfo;
  debugInfo?: {
    bytecode?: string;
    abiVersion?: string;
    deploymentBlock?: number;
    networkId?: string;
    suggestions?: string[];
  };
}

export interface ContractUpdateEvent {
  type: 'address' | 'abi';
  contractName: string;
  oldValue?: string | any[];
  newValue: string | any[];
  timestamp: number;
}

export class ContractUpdateService extends EventEmitter {
  private provider: ethers.BrowserProvider | null = null;
  private currentAddresses: ContractAddresses | null = null;
  private currentABIs: ContractABIs | null = null;
  private watchInterval: NodeJS.Timeout | null = null;
  private isWatching: boolean = false;
  private deploymentValidator: ContractDeploymentValidator | null = null;
  private networkDetector: NetworkEnvironmentDetector | null = null;
  private errorHandler: DevelopmentModeErrorHandler;
  private developmentMode: boolean;

  constructor() {
    super();
    this.developmentMode = process.env.NODE_ENV === 'development';
    this.errorHandler = DevelopmentModeErrorHandler.getInstance();

    this.loadCurrentConfiguration();
  }

  private async ensureInitialized() {
    if (!this.provider) {
      await this.initializeProvider();
    }

    if (!this.provider) {
      throw new Error('Provider not initialized');
    }
  }

  private async initializeProvider() {
    if (typeof window !== 'undefined' && window.ethereum) {
      this.provider = new ethers.BrowserProvider(window.ethereum);

      // Initialize development mode tools
      if (this.provider) {
        this.deploymentValidator = new ContractDeploymentValidator(
          this.provider
        );
        this.networkDetector = new NetworkEnvironmentDetector(this.provider);

        // Log network environment in development mode
        if (this.developmentMode) {
          this.logNetworkEnvironment();
        }
      }
    }
  }

  /**
   * Load current contract addresses and ABIs from constants and ABI files
   */
  private async loadCurrentConfiguration() {
    try {
      // Load current addresses from constants
      const constants = await import('../constants');
      this.currentAddresses = {
        swmAddress: constants.swmAddress,
        wethAddress: constants.wethAddress,
        llmnftAddress: constants.llmnftAddress,
        hfswmAddress: constants.hfswmAddress,
        poolAddress: constants.poolAddress,
        usdtAddress: constants.usdtAddress,
        receiverAddress: constants.receiverAddress,
      };

      // Load current ABIs
      const abis = await import('../abis');
      this.currentABIs = {
        erc20: abis.erc20Abi,
        pool: abis.poolAbi,
        llmnft: abis.llmnftAbi,
      };

      this.emit('configurationLoaded', {
        addresses: this.currentAddresses,
        abis: this.currentABIs,
      });
    } catch (error) {
      console.error('Error loading current configuration:', error);
      this.emit(
        'error',
        new Error(
          `Failed to load configuration: ${error instanceof Error ? error.message : 'Unknown error'}`
        )
      );
    }
  }

  /**
   * Start watching for address and ABI updates
   */
  startWatching(intervalMs: number = 30000) {
    if (this.isWatching) {
      console.warn('Contract update service is already watching');
      return;
    }

    this.isWatching = true;
    this.watchInterval = setInterval(() => {
      this.checkForUpdates();
    }, intervalMs);

    console.log(
      `Started watching for contract updates (interval: ${intervalMs}ms)`
    );
    this.emit('watchingStarted', { interval: intervalMs });
  }

  /**
   * Stop watching for updates
   */
  stopWatching() {
    if (this.watchInterval) {
      clearInterval(this.watchInterval);
      this.watchInterval = null;
    }

    this.isWatching = false;
    console.log('Stopped watching for contract updates');
    this.emit('watchingStopped');
  }

  /**
   * Check for updates by comparing current configuration with loaded modules
   */
  private async checkForUpdates() {
    try {
      // Force reload modules to get latest values
      delete require.cache[require.resolve('../constants')];
      delete require.cache[require.resolve('../abis')];

      const newConstants = await import('../constants');
      const newAbis = await import('../abis');

      // Check for address updates
      const newAddresses: ContractAddresses = {
        swmAddress: newConstants.swmAddress,
        wethAddress: newConstants.wethAddress,
        llmnftAddress: newConstants.llmnftAddress,
        hfswmAddress: newConstants.hfswmAddress,
        poolAddress: newConstants.poolAddress,
        usdtAddress: newConstants.usdtAddress,
        receiverAddress: newConstants.receiverAddress,
      };

      this.checkAddressUpdates(newAddresses);

      // Check for ABI updates
      const newABIs: ContractABIs = {
        erc20: newAbis.erc20Abi,
        pool: newAbis.poolAbi,
        llmnft: newAbis.llmnftAbi,
      };

      this.checkABIUpdates(newABIs);
    } catch (error) {
      console.error('Error checking for updates:', error);
      this.emit('error', error);
    }
  }

  /**
   * Check for address updates and emit events
   */
  private checkAddressUpdates(newAddresses: ContractAddresses) {
    if (!this.currentAddresses) return;

    for (const [key, newAddress] of Object.entries(newAddresses)) {
      const oldAddress = this.currentAddresses[key as keyof ContractAddresses];

      if (oldAddress && oldAddress !== newAddress) {
        const updateEvent: ContractUpdateEvent = {
          type: 'address',
          contractName: key,
          oldValue: oldAddress,
          newValue: newAddress,
          timestamp: Date.now(),
        };

        console.log(
          `Address update detected for ${key}: ${oldAddress} → ${newAddress}`
        );
        this.emit('addressUpdate', updateEvent);
        this.emit('update', updateEvent);
      }
    }

    this.currentAddresses = newAddresses;
  }

  /**
   * Check for ABI updates and emit events
   */
  private checkABIUpdates(newABIs: ContractABIs) {
    if (!this.currentABIs) return;

    for (const [contractName, newABI] of Object.entries(newABIs)) {
      const oldABI = this.currentABIs[contractName];

      if (oldABI && JSON.stringify(oldABI) !== JSON.stringify(newABI)) {
        const updateEvent: ContractUpdateEvent = {
          type: 'abi',
          contractName,
          oldValue: oldABI,
          newValue: newABI,
          timestamp: Date.now(),
        };

        console.log(`ABI update detected for ${contractName}`);
        this.emit('abiUpdate', updateEvent);
        this.emit('update', updateEvent);
      }
    }

    this.currentABIs = newABIs;
  }

  /**
   * Validate contract deployment at given address with enhanced development mode debugging
   */
  async validateContractDeployment(
    contractName: string,
    address: string,
    expectedABI?: any[]
  ): Promise<ContractValidationResult> {
    try {
      if (!this.provider) {
        await this.initializeProvider();
      }

      if (!this.provider || !this.deploymentValidator) {
        const error = 'Provider or deployment validator not available';

        if (this.developmentMode) {
          const devError = this.errorHandler.handleError(
            new Error(error),
            `validateContractDeployment:${contractName}`
          );

          return {
            isValid: false,
            contractName,
            address,
            error,
            debugInfo: {
              suggestions: devError.suggestions,
            },
          };
        }

        return {
          isValid: false,
          contractName,
          address,
          error,
        };
      }

      // Use enhanced deployment validator
      const validationResult =
        await this.deploymentValidator.validateContractDeployment(
          contractName,
          address,
          expectedABI
        );

      // Convert to our interface format
      const result: ContractValidationResult = {
        isValid: validationResult.isValid,
        contractName,
        address,
        error: validationResult.error,
      };

      // Add debug info in development mode
      if (this.developmentMode && validationResult.debugInfo) {
        result.debugInfo = {
          bytecode: validationResult.debugInfo.bytecode,
          abiVersion: validationResult.debugInfo.abiVersion,
          deploymentBlock: validationResult.debugInfo.deploymentBlock,
          networkId: validationResult.debugInfo.networkId,
        };

        // Add development-specific suggestions
        if (!validationResult.isValid) {
          result.debugInfo.suggestions = [
            'Check if contract is deployed to current network',
            'Verify contract address in configuration',
            'Ensure ABI matches deployed contract',
            'Check if local blockchain is running (for local networks)',
            'Try redeploying contract if using local development',
          ];
        }
      }

      // Enhanced ABI compatibility checking in development mode
      if (expectedABI && this.developmentMode && this.provider) {
        try {
          const abiCheck = await AbiCompatibilityChecker.checkAbiCompatibility(
            expectedABI,
            address,
            this.provider
          );

          if (!abiCheck.isCompatible && result.debugInfo) {
            result.debugInfo.suggestions = [
              ...(result.debugInfo.suggestions || []),
              `Missing functions: ${abiCheck.missingFunctions.join(', ')}`,
              ...abiCheck.suggestions,
            ];
          }
        } catch (abiError) {
          console.warn('ABI compatibility check failed:', abiError);
        }
      }

      return result;
    } catch (error) {
      const devError = this.errorHandler.handleError(
        error,
        `validateContractDeployment:${contractName}`
      );

      const result: ContractValidationResult = {
        isValid: false,
        contractName,
        address,
        error: devError.message,
      };

      if (this.developmentMode) {
        result.debugInfo = {
          suggestions: devError.suggestions,
        };
      }

      return result;
    }
  }

  /**
   * Validate all current contract deployments
   */
  async validateAllContracts(): Promise<ContractValidationResult[]> {
    if (!this.currentAddresses || !this.currentABIs) {
      throw new Error('Configuration not loaded');
    }

    const results: ContractValidationResult[] = [];

    // Validate SWM token
    results.push(
      await this.validateContractDeployment(
        'swm',
        this.currentAddresses.swmAddress,
        this.currentABIs.erc20
      )
    );

    // Validate Pool contract
    results.push(
      await this.validateContractDeployment(
        'pool',
        this.currentAddresses.poolAddress,
        this.currentABIs.pool
      )
    );

    // Validate LLMNFT contract
    results.push(
      await this.validateContractDeployment(
        'llmnft',
        this.currentAddresses.llmnftAddress,
        this.currentABIs.llmnft
      )
    );

    // Validate other ERC20 tokens
    results.push(
      await this.validateContractDeployment(
        'weth',
        this.currentAddresses.wethAddress,
        this.currentABIs.erc20
      )
    );

    results.push(
      await this.validateContractDeployment(
        'hfswm',
        this.currentAddresses.hfswmAddress,
        this.currentABIs.erc20
      )
    );

    return results;
  }

  /**
   * Handle address update events from external scripts
   */
  handleAddressUpdate(newAddresses: Partial<ContractAddresses>) {
    if (!this.currentAddresses) {
      console.warn('Current addresses not loaded, cannot handle update');
      return;
    }

    const updatedAddresses = { ...this.currentAddresses, ...newAddresses };
    this.checkAddressUpdates(updatedAddresses);
  }

  /**
   * Handle ABI update events from external scripts
   */
  handleABIUpdate(contractName: string, newABI: any[]) {
    if (!this.currentABIs) {
      console.warn('Current ABIs not loaded, cannot handle update');
      return;
    }

    const updatedABIs = { ...this.currentABIs, [contractName]: newABI };
    this.checkABIUpdates(updatedABIs);
  }

  /**
   * Get current contract addresses
   */
  getCurrentAddresses(): ContractAddresses | null {
    return this.currentAddresses;
  }

  /**
   * Get current ABIs
   */
  getCurrentABIs(): ContractABIs | null {
    return this.currentABIs;
  }

  /**
   * Check if service is currently watching for updates
   */
  isCurrentlyWatching(): boolean {
    return this.isWatching;
  }

  /**
   * Force refresh configuration
   */
  async refreshConfiguration() {
    await this.loadCurrentConfiguration();
  }

  /**
   * Integration hook for address updater script
   */
  static createAddressUpdateHook() {
    return (newAddresses: Partial<ContractAddresses>) => {
      // This would be called by the address updater script
      const service = contractUpdateService;
      service.handleAddressUpdate(newAddresses);
    };
  }

  /**
   * Integration hook for ABI updater script
   */
  static createABIUpdateHook() {
    return (contractName: string, newABI: any[]) => {
      // This would be called by the ABI updater script
      const service = contractUpdateService;
      service.handleABIUpdate(contractName, newABI);
    };
  }

  /**
   * Log network environment information in development mode
   */
  private async logNetworkEnvironment() {
    if (!this.developmentMode || !this.networkDetector) return;

    try {
      const env = await this.networkDetector.detectEnvironment();
      const indicators =
        await this.networkDetector.generateEnvironmentIndicators();

      console.group('🌐 Network Environment (Development Mode)');
      console.log('Environment:', env);
      console.log('Indicators:', indicators);

      if (indicators.warnings.length > 0) {
        console.warn('Warnings:', indicators.warnings);
      }

      console.groupEnd();

      // Emit environment info for UI components
      this.emit('environmentDetected', { env, indicators });
    } catch (error) {
      console.error('Failed to detect network environment:', error);
    }
  }

  /**
   * Get network environment information
   */
  async getNetworkEnvironment() {
    if (!this.networkDetector) {
      throw new Error('Network detector not initialized');
    }

    return await this.networkDetector.detectEnvironment();
  }

  /**
   * Get environment indicators for UI display
   */
  async getEnvironmentIndicators() {
    if (!this.networkDetector) {
      throw new Error('Network detector not initialized');
    }

    return await this.networkDetector.generateEnvironmentIndicators();
  }

  /**
   * Enhanced validation with development mode debugging
   */
  async validateAllContractsEnhanced(): Promise<{
    results: ContractValidationResult[];
    summary: {
      totalContracts: number;
      validContracts: number;
      invalidContracts: number;
      warnings: string[];
    };
    environment?: any;
  }> {
    const results = await this.validateAllContracts();

    const summary = {
      totalContracts: results.length,
      validContracts: results.filter(r => r.isValid).length,
      invalidContracts: results.filter(r => !r.isValid).length,
      warnings: results
        .filter(r => !r.isValid)
        .map(r => `${r.contractName}: ${r.error}`),
    };

    let environment;
    if (this.developmentMode && this.networkDetector) {
      try {
        environment = await this.networkDetector.detectEnvironment();
      } catch (error) {
        console.warn('Could not get environment info:', error);
      }
    }

    // Log summary in development mode
    if (this.developmentMode) {
      console.group('📋 Contract Validation Summary');
      console.log('Results:', results);
      console.log('Summary:', summary);
      if (environment) console.log('Environment:', environment);
      console.groupEnd();
    }

    return {
      results,
      summary,
      environment,
    };
  }

  /**
   * Development mode contract debugging helper
   */
  async debugContract(
    contractName: string,
    address: string,
    abi?: any[]
  ): Promise<{
    validation: ContractValidationResult;
    networkInfo: any;
    suggestions: string[];
  }> {
    if (!this.developmentMode) {
      throw new Error('Debug mode only available in development');
    }

    const validation = await this.validateContractDeployment(
      contractName,
      address,
      abi
    );

    let networkInfo;
    try {
      networkInfo = await this.getNetworkEnvironment();
    } catch (error) {
      networkInfo = { error: 'Could not get network info' };
    }

    const suggestions: string[] = [];

    if (!validation.isValid) {
      suggestions.push('Contract validation failed');
      suggestions.push(...(validation.debugInfo?.suggestions || []));
    }

    if (networkInfo.networkType === 'local') {
      suggestions.push('Using local network - ensure contracts are deployed');
    } else if (networkInfo.networkType === 'unknown') {
      suggestions.push(
        'Unknown network detected - check network configuration'
      );
    }

    return {
      validation,
      networkInfo,
      suggestions,
    };
  }

  /**
   * Check if development mode debugging is available
   */
  isDevelopmentMode(): boolean {
    return this.developmentMode;
  }

  /**
   * Get development mode error handler
   */
  getErrorHandler(): DevelopmentModeErrorHandler {
    return this.errorHandler;
  }

  /**
   * Cleanup resources
   */
  destroy() {
    this.stopWatching();
    this.removeAllListeners();
  }
}

// Export singleton instance
export const contractUpdateService = new ContractUpdateService();

// HMR cleanup to avoid disposed module requires
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const module: any;
if (typeof module !== 'undefined' && module.hot) {
  module.hot.dispose(() => {
    try {
      contractUpdateService.destroy();
    } catch (e) {
      // no-op
    }
  });
}
